import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ColumnDef, DataTable } from '../../Components/Table/DataTable';
import { Header } from '../../Components/Header/Header';
import { FreshnessBadge } from '../../Components/Vending/FreshnessBadge';
import vendingApiClient from '../../Services/VendingApi';
import { formatUTC } from '../../Services/FormatUtc';
import { PageContainer } from '../../Styles/AppStyles';
import { COLORS } from '../../Styles/Colors';
import { VendingMeta } from '../../Types/vending';

const SEARCH_DEBOUNCE_MS = 400;

interface AuditRow {
  id: number;
  user_id: number | null;
  username: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  previous_value: unknown;
  new_value: unknown;
  ip_address: string | null;
  created_at: string;
}

interface AuditListResponse {
  data: AuditRow[];
  meta: VendingMeta & { total?: number };
}

/** Spec §54 action examples → readable labels for known verbs. */
const ACTION_LABELS: Record<string, string> = {
  machine_map: 'Machine reassigned',
  machine_unmap: 'Machine unmapped',
  product_map: 'Product mapping changed',
  product_unmap: 'Product unmapped',
  product_auto_map: 'Product mapping changed',
  employee_map: 'Employee mapping changed',
  employee_unmap: 'Employee unmapped',
  alert_ack: 'Alert acknowledged',
  alert_acknowledge: 'Alert acknowledged',
  alert_resolve: 'Alert resolved',
  alert_resolved: 'Alert resolved',
  restock_complete: 'Restock completed',
  restock_completed: 'Restock completed',
  config_change: 'Configuration changed',
  configuration_changed: 'Configuration changed',
};

const actionStyle = (action: string) => {
  const a = action.toLowerCase();
  if (a.includes('unmap') || a.includes('delete') || a.includes('remove')) {
    return { color: COLORS.red, bg: `${COLORS.red}12` };
  }
  if (a.includes('map') || a.includes('create') || a.includes('add') || a.includes('complete')) {
    return { color: COLORS.success, bg: `${COLORS.success}18` };
  }
  if (a.includes('auto')) {
    return { color: COLORS.teal, bg: `${COLORS.teal}14` };
  }
  if (a.includes('ack') || a.includes('resolve') || a.includes('update') || a.includes('config')) {
    return { color: COLORS.orange, bg: `${COLORS.orange}18` };
  }
  return { color: COLORS.darkGray, bg: `${COLORS.lightGray}18` };
};

const formatActionLabel = (action: string) =>
  ACTION_LABELS[action]
  || action.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatEntityLabel = (entityType: string) =>
  entityType.replace(/^vending_/, '').replace(/_/g, ' ');

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch {
      return null;
    }
  }
  return null;
};

/** Compact one-line preview for Previous / New value columns (spec §54). */
const formatValuePreview = (value: unknown): string => {
  if (value == null || value === '') return '—';

  const obj = asRecord(value);
  if (obj) {
    const parts: string[] = [];
    if (obj.customer_name) parts.push(String(obj.customer_name));
    if (obj.location_name) parts.push(String(obj.location_name));
    if (obj.cid != null) parts.push(`CID ${obj.cid}`);
    if (obj.ovid != null) parts.push(`OVID ${obj.ovid}`);
    if (obj.pid != null) parts.push(`PID ${obj.pid}`);
    if (obj.uid != null) parts.push(`UID ${obj.uid}`);
    if (obj.aims_product && typeof obj.aims_product === 'object') {
      const p = obj.aims_product as { product_code?: string; name?: string };
      parts.push(p.product_code || p.name || 'AIMS product');
    }
    if (obj.aims_user && typeof obj.aims_user === 'object') {
      const u = obj.aims_user as { username?: string; name?: string };
      parts.push(u.username || u.name || 'AIMS user');
    }
    if (obj.location_map_status) parts.push(String(obj.location_map_status));
    if (obj.map_status) parts.push(String(obj.map_status));
    if (obj.map_confidence) parts.push(String(obj.map_confidence));
    if (parts.length) return parts.join(' · ');
    try {
      const json = JSON.stringify(obj);
      return json.length > 120 ? `${json.slice(0, 117)}…` : json;
    } catch {
      return '—';
    }
  }

  if (typeof value === 'string') {
    return value.length > 120 ? `${value.slice(0, 117)}…` : value;
  }

  try {
    const json = JSON.stringify(value);
    return json.length > 120 ? `${json.slice(0, 117)}…` : json;
  } catch {
    return String(value);
  }
};

const formatJson = (value: unknown) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const ValueCell = ({ value }: { value: unknown }) => {
  const preview = formatValuePreview(value);
  const empty = preview === '—';
  return (
    <Typography
      variant="body2"
      noWrap
      title={empty ? undefined : preview}
      sx={{
        color: empty ? COLORS.lightGray : COLORS.darkGray,
        maxWidth: '100%',
      }}
    >
      {preview}
    </Typography>
  );
};

const COMMON_ACTIONS = [
  'machine_map',
  'machine_unmap',
  'product_map',
  'product_unmap',
  'product_auto_map',
  'employee_map',
  'employee_unmap',
  'alert_acknowledge',
  'alert_resolve',
  'restock_complete',
];

const COMMON_ENTITIES = ['vending_machine', 'vending_product', 'vending_employee', 'vending_alert'];

export const VendingAuditLogPage = () => {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [detail, setDetail] = useState<AuditRow | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (actionFilter) params.set('action', actionFilter);
      if (entityFilter) params.set('entity_type', entityFilter);
      if (debouncedSearch) params.set('username', debouncedSearch);
      const response = await vendingApiClient(`/audit?${params.toString()}`);
      const json: AuditListResponse = await response.json();
      setRows(json.data);
      setAsOf(json.meta?.as_of ?? null);
      setTotal(json.meta?.total ?? json.data.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit log');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [actionFilter, entityFilter, debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const actionOptions = useMemo(() => {
    const set = new Set([...COMMON_ACTIONS, ...rows.map((r) => r.action).filter(Boolean)]);
    return Array.from(set).sort();
  }, [rows]);

  const entityOptions = useMemo(() => {
    const set = new Set([...COMMON_ENTITIES, ...rows.map((r) => r.entity_type).filter(Boolean)]);
    return Array.from(set).sort();
  }, [rows]);

  const hasActiveFilters = Boolean(debouncedSearch || actionFilter || entityFilter);

  const clearFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setActionFilter('');
    setEntityFilter('');
  };

  // Spec §54 capture columns: User, Action, Entity, Previous value, New value, Timestamp
  const columns: ColumnDef<AuditRow>[] = useMemo(() => [
    {
      field: 'username',
      headerName: 'User',
      width: 140,
      minWidth: 110,
      searchable: true,
      valueGetter: (_v, row) => row.username || '—',
      renderCell: (params) => (
        <Typography variant="body2" noWrap title={params.row.username || '—'}>
          {params.row.username || '—'}
        </Typography>
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 200,
      minWidth: 160,
      valueGetter: (_v, row) => formatActionLabel(row.action),
      renderCell: (params) => {
        const style = actionStyle(params.row.action);
        return (
          <Chip
            size="small"
            label={formatActionLabel(params.row.action)}
            sx={{
              height: 24,
              fontSize: '0.7rem',
              fontWeight: 700,
              bgcolor: style.bg,
              color: style.color,
            }}
          />
        );
      },
    },
    {
      field: 'entity_type',
      headerName: 'Entity',
      width: 150,
      minWidth: 120,
      valueGetter: (_v, row) => `${formatEntityLabel(row.entity_type)} #${row.entity_id}`,
      renderCell: (params) => (
        <Box sx={{ py: 0.5, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ color: COLORS.darkGray, textTransform: 'capitalize', lineHeight: 1.3 }}
          >
            {formatEntityLabel(params.row.entity_type)}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: COLORS.teal, display: 'block' }}
          >
            #{params.row.entity_id}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'previous_value',
      headerName: 'Previous value',
      width: 240,
      minWidth: 180,
      valueGetter: (_v, row) => formatValuePreview(row.previous_value),
      renderCell: (params) => <ValueCell value={params.row.previous_value} />,
    },
    {
      field: 'new_value',
      headerName: 'New value',
      width: 240,
      minWidth: 180,
      valueGetter: (_v, row) => formatValuePreview(row.new_value),
      renderCell: (params) => <ValueCell value={params.row.new_value} />,
    },
    {
      field: 'created_at',
      headerName: 'Timestamp',
      width: 190,
      minWidth: 170,
      valueGetter: (_v, row) => formatUTC(row.created_at),
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: COLORS.darkGray, whiteSpace: 'nowrap' }}>
          {formatUTC(params.row.created_at)} UTC
        </Typography>
      ),
    },
  ], []);

  const tableRows = useMemo(
    () => rows.map((r) => ({
      ...r,
      username: r.username || '—',
    })),
    [rows],
  );

  const subtitle = useMemo(() => {
    const parts = [`${total} entries`];
    if (asOf) parts.push(`As of ${formatUTC(asOf)} UTC`);
    return parts.join(' · ');
  }, [total, asOf]);

  return (
    <PageContainer>
      <Header title="Audit Log" subtitle={subtitle} count={rows.length}>
        <FreshnessBadge asOf={asOf} />
        <Button variant="outlined" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Header>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            pt: { xs: 2, md: 3 },
            pb: 2,
            flexShrink: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            label="User"
            placeholder="Filter by username…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: 220, flex: { xs: '1 1 100%', md: '0 1 240px' } }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Action</InputLabel>
            <Select
              label="Action"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <MenuItem value="">All actions</MenuItem>
              {actionOptions.map((action) => (
                <MenuItem key={action} value={action}>{formatActionLabel(action)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Entity</InputLabel>
            <Select
              label="Entity"
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
            >
              <MenuItem value="">All entities</MenuItem>
              {entityOptions.map((entity) => (
                <MenuItem key={entity} value={entity}>{formatEntityLabel(entity)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Button variant="text" onClick={clearFilters} sx={{ textTransform: 'none' }}>
              Clear Filters
            </Button>
          )}
        </Box>

        {loading && rows.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6, flexShrink: 0 }}>
            <CircularProgress sx={{ color: COLORS.teal }} />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ px: { xs: 2, md: 3 }, flexShrink: 0 }}>
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}
            >
              {error}
            </Alert>
          </Box>
        )}

        {!loading && !error && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%' }}>
            <DataTable
              columns={columns}
              data={tableRows}
              pageKey="vending-audit-trail"
              idField="id"
              loading={loading}
              rowHeight={56}
              onRowClick={(row) => setDetail(rows.find((r) => r.id === row.id) ?? row)}
            />
          </Box>
        )}
      </Box>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {detail ? formatActionLabel(detail.action) : 'Audit detail'}
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: COLORS.lightGray, fontWeight: 700, textTransform: 'uppercase' }}>
                    User
                  </Typography>
                  <Typography variant="body2">{detail.username || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: COLORS.lightGray, fontWeight: 700, textTransform: 'uppercase' }}>
                    Action
                  </Typography>
                  <Typography variant="body2">{formatActionLabel(detail.action)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: COLORS.lightGray, fontWeight: 700, textTransform: 'uppercase' }}>
                    Entity
                  </Typography>
                  <Typography variant="body2">
                    {formatEntityLabel(detail.entity_type)} · #{detail.entity_id}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: COLORS.lightGray, fontWeight: 700, textTransform: 'uppercase' }}>
                    Timestamp
                  </Typography>
                  <Typography variant="body2">{formatUTC(detail.created_at)} UTC</Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: COLORS.lightGray, fontWeight: 700, textTransform: 'uppercase' }}>
                  Previous value
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: '#f8f9fa',
                    border: `1px solid ${COLORS.lightGray}35`,
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 220,
                    m: 0,
                  }}
                >
                  {formatJson(detail.previous_value)}
                </Box>
              </Box>

              <Box>
                <Typography variant="caption" sx={{ color: COLORS.lightGray, fontWeight: 700, textTransform: 'uppercase' }}>
                  New value
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    mt: 0.5,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: `${COLORS.teal}08`,
                    border: `1px solid ${COLORS.teal}30`,
                    fontSize: '0.75rem',
                    overflow: 'auto',
                    maxHeight: 220,
                    m: 0,
                  }}
                >
                  {formatJson(detail.new_value)}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDetail(null)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};
